import classnames from 'classnames';
import { any, assoc, equals, find, length, map, propEq } from 'ramda';
import React, { CSSProperties, FC, MouseEvent, useCallback, useEffect, useReducer, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAsync } from 'react-use';
import { LassoItemMargin } from '../../../../../constants/Lasso';
import { Layout } from '../../../../../constants/Layout';
import { ModalName } from '../../../../../constants/ModalName';
import { ExternalSource } from '../../../../../constants/Source';
import { SystemFolderId } from '../../../../../constants/SystemFolderId';
import { EmptyState } from '../../../../../containers/FilesImporter/components/FilesViewer/EmptyState';
import filesViewerStyles from '../../../../../containers/FilesImporter/components/FilesViewer/FilesViewer.scss';
import { FilesGallery } from '../../../../../containers/FilesImporter/components/FilesViewer/layouts/FilesGalleryLayout';
import { MediaTypeSelect } from '../../../../../containers/FilesManager/MediaTypeSelect';
import { BI_ERRORS, errorLogger } from '../../../../../services/loggers/errorLogger';
import { useAdapter } from '../../../../../utils/hooks/useAdapter';
import { useBoards } from '../../../../../utils/hooks/useBoards';
import { useEffectButSkipMount } from '../../../../../utils/hooks/useEffectButSkipMount';
import { useModalHandlersAndOptions } from '../../../../../utils/hooks/useModal';
import { MediaManagerSDKAnyItem } from '../../../../../utils/media-manager-sdk';
import { isCurrentSiteOnlyBoard } from '../../../../../utils/media-manager-sdk/boards';
import { parseSiteMediaToken } from '../../../../../utils/siteMediaToken';
import { Breadcrumbs } from '../../../Breadcrumbs';
import { FullHeightLoader } from '../../../FullHeightLoader';
import { AutoComplete, Button } from '../../../index';
import { Lasso } from '../../../Lasso';
import lassoSelectedAreaStyles from '../../../Lasso/withLassoSelectedAreaStyle/LassoSelectedArea.scss';
import { Loader } from '../../../Loader';
import { Search } from '../../../Search';
import styles from './SiteFilesViewer.scss';
import { IInner, IOuter } from './SiteFilesViewer.types';
import { Text } from '@wix/design-system';

interface ISite {
  id: string;
  value: string;
  siteMediaToken: string;
}

interface ISiteListState {
  siteList: ISite[];
  activeSite: ISite;
}

const enum ActionType {
  SetSiteList = 'set-site-list',
  SetActiveSite = 'set-active-site',
  AddMoreSites = 'add-to-site-list',
}

const reducer = (state: ISiteListState, action) => {
  switch (action.type) {
    case ActionType.SetSiteList:
      return action.payload;

    case ActionType.SetActiveSite:
      return { ...state, activeSite: action.payload };

    case ActionType.AddMoreSites:
      return { ...state, siteList: [...state.siteList, ...action.payload] };

    default:
      return state;
  }
};

const scrollStyle: CSSProperties = { position: 'relative', overflowX: 'hidden', flexDirection: 'column' };

export const SiteFilesViewerComponent: FC<IOuter & IInner> = ({
  source,
  selectedItems,
  refreshFolder,
  openFolder: openFolderFromProps,
  className,
  folderPath,
  items,
  isLoading,
  isSearch,
  toggleItemSelection,
  selectItemsUntil,
  selectItem,
  selectItems,
  mediaTypes,
  setMediaType,
  areNotAllMediaTypesSelected,
  selectAllMediaTypes,
  areNextFilesLoading,
  hasMoreItems,
  loadMoreItems,
  selectedItemsCount,
  isItemSelected,
  clearItemsSelection,
  isNotAuthorized,
  onSourceSessionExpired,
  searchQuery,
  setSearchQuery,
  clearSearch,
  updateSiteMediaToken,
  siteMediaToken,
  boards,
  onSelectItems,
}) => {
  useEffectButSkipMount(refreshFolder, [mediaTypes]);

  const {
    closeModal,
    additionalOptions: { shouldUpdateItemsInFolder, clickOrigin },
  } = useModalHandlersAndOptions(ModalName.AddFilesToBoard);

  const { addItems } = useBoards();
  const isSingleLevelNesting = equals(length(folderPath), 1);
  const isRoot = isSingleLevelNesting;

  const handleSelectItems = useCallback(
    (selectedItems) => {
      const itemsWithSiteToken = map<MediaManagerSDKAnyItem, MediaManagerSDKAnyItem>(
        assoc('siteMediaToken', siteMediaToken),
        selectedItems,
      );

      addItems(boards, itemsWithSiteToken, { clickOrigin })
        .then(() => {
          onSelectItems({ shouldUpdateItemsInFolder, items: selectedItems });
        })
        .catch(errorLogger.createLoggingHandler(BI_ERRORS.mediaManagerSDK.addItemsToBoard));

      closeModal();
    },
    [closeModal, clickOrigin, addItems, boards, shouldUpdateItemsInFolder, onSelectItems, siteMediaToken],
  );

  const { formatMessage } = useIntl();

  const wixSiteAdapter = useAdapter(ExternalSource.WixSite);
  const { loading: isSitesListLoading, value: siteListData } = useAsync(
    () => wixSiteAdapter.folders.list(SystemFolderId.WixSites, { includeCurrentSite: true }),
    [],
  );

  const [siteListState, dispatch] = useReducer(reducer, { siteList: [], activeSite: undefined });
  const [fetchSitesCursor, setFetchSitesCursor] = useState(null);

  const parseSiteListData = map(({ id, name, siteId }) => ({
    id: siteId,
    value: name,
    siteMediaToken: JSON.parse(id).siteMediaToken,
  }));

  useEffect(() => {
    if (isSitesListLoading || !siteListData) {
      return;
    }
    setFetchSitesCursor(siteListData.paging?.cursor);
    const parsedSiteListData = parseSiteListData(siteListData.data);
    const currentSiteId = parseSiteMediaToken(siteMediaToken).metaSiteId;
    const activeSite = find(propEq('id', currentSiteId))(parsedSiteListData);

    dispatch({
      type: ActionType.SetSiteList,
      payload: {
        siteList: parsedSiteListData,
        activeSite,
      },
    });
  }, [isSitesListLoading]);

  const loadMoreSites = async () => {
    const moreSites = await wixSiteAdapter.folders.list(SystemFolderId.WixSites, {
      paging: { cursor: fetchSitesCursor },
    });
    setFetchSitesCursor(moreSites.paging?.cursor);
    dispatch({
      type: ActionType.AddMoreSites,
      payload: parseSiteListData(moreSites.data),
    });
  };

  const [siteSelectionState, updateSiteSelection] = useState('');

  const handleChangeSite = useCallback(
    (event) => {
      updateSiteSelection(event.target.value);
    },
    [updateSiteSelection],
  );

  const openFolder = useCallback(
    (folder) => {
      clearSearch();
      openFolderFromProps(folder);
    },
    [openFolderFromProps, clearSearch],
  );

  const openSiteRootFolder = useCallback(() => {
    openFolder(folderPath[0]);
  }, [openFolder, folderPath]);

  const handleSelectNewSite = useCallback(
    ({ id }) => {
      const newActiveSite = find<ISite>(propEq('id', id))(siteListState.siteList);
      if (!newActiveSite) {
        return;
      }
      clearSearch();
      openSiteRootFolder();
      selectAllMediaTypes();

      updateSiteSelection(newActiveSite.value);
      updateSiteMediaToken(newActiveSite.siteMediaToken);
    },
    [siteListState, selectAllMediaTypes, clearSearch, openSiteRootFolder, updateSiteSelection, updateSiteMediaToken],
  );

  useEffect(() => {
    const { activeSite } = siteListState;
    if (activeSite && activeSite.value) {
      updateSiteSelection(activeSite.value);
    }
  }, [siteListState]);

  const isBreadcrumbsVisible = length(folderPath) > 1;
  const hasNoItems = !isLoading && !items.length;
  const isSiteSelectorHidden = any(isCurrentSiteOnlyBoard)(boards || []);

  const submitSelectedItems = useCallback(() => handleSelectItems(selectedItems), [handleSelectItems, selectedItems]);
  const submitItemOnDoubleClick = useCallback((item) => handleSelectItems([item]), [handleSelectItems]);

  const handleBreadcrumbsClick = useCallback(
    (folder) => {
      clearSearch();
      openFolder(folder);
    },
    [openFolder, clearSearch],
  );

  const handleItemClick = useCallback(
    (item, event: MouseEvent<HTMLElement>) => {
      const biParams = { tabName: `add_to_board` };

      const { id } = item;
      const { ctrlKey, metaKey, shiftKey } = event;

      if (ctrlKey || metaKey) {
        toggleItemSelection(id, biParams);
      } else if (shiftKey) {
        selectItemsUntil(id);
      } else {
        selectItem(id, biParams);
      }
    },
    [toggleItemSelection, selectItemsUntil, selectItem],
  );

  const handleSearchStart = useCallback(
    (searchQuery) => {
      openSiteRootFolder();

      setSearchQuery(searchQuery);
    },
    [openSiteRootFolder, setSearchQuery],
  );

  useEffect(() => {
    if (isNotAuthorized) {
      onSourceSessionExpired(source);
    }
  }, [isNotAuthorized, onSourceSessionExpired, source]);

  const isItemDisabled = (_item) => false;

  return (
    <div className={classnames(filesViewerStyles.wrapper, className)}>
      {!isSiteSelectorHidden && !isSitesListLoading && (
        <div className={styles.topRow}>
          <div className={styles.siteSelectionHolder}>
            <Text tagName="label" className={styles.chooseSitesLabel}>
              <FormattedMessage id="boards.modal.add-files.choose-sites.label" />
            </Text>

            <AutoComplete
              infiniteScroll
              dataHook="add-to-board-choose-sites"
              className={styles.siteSelection}
              options={siteListState.siteList}
              hasMore={!!fetchSitesCursor}
              loadMore={loadMoreSites}
              value={siteSelectionState}
              onChange={handleChangeSite}
              onSelect={handleSelectNewSite}
              predicate={(option) => `${option.value}`.toLowerCase().indexOf(siteSelectionState.toLowerCase()) !== -1}
              popoverProps={{
                minWidth: 282,
                placement: 'bottom-start',
              }}
            />
          </div>

          <div className={filesViewerStyles.topRowActions}>
            <MediaTypeSelect
              mediaTypes={mediaTypes}
              onMediaTypeSelect={setMediaType}
              isDisabled={hasNoItems && !areNotAllMediaTypesSelected}
            />

            <div className={styles.actionsDivider} />

            <div className={styles.searchWrapper}>
              <Search
                value={searchQuery}
                onEnterPressed={handleSearchStart}
                onClearButtonClick={clearSearch}
                key={searchQuery}
                placeholder={formatMessage({ id: 'boards.modal.add-files.search.input-placeholder' })}
                isExpandable
              />
            </div>
          </div>
        </div>
      )}

      {isBreadcrumbsVisible && (
        <Breadcrumbs
          className={styles.breadcrumbs}
          folderPath={folderPath}
          source={source}
          onClick={handleBreadcrumbsClick}
          dropDisabled
        />
      )}

      {hasNoItems ? (
        <EmptyState
          source={ExternalSource.WixSite}
          isSearch={isSearch}
          searchQuery={searchQuery}
          isRoot={isRoot}
          isWixSitesRoot={false}
          areNotAllMediaTypesSelected={areNotAllMediaTypesSelected}
          clearAllExtendedFilters={selectAllMediaTypes}
        />
      ) : (
        <div className={filesViewerStyles.content}>
          <div className={classnames(filesViewerStyles.items, isLoading && filesViewerStyles.isLoadingItems)}>
            {isLoading ? (
              <div className={filesViewerStyles.loader}>
                <FullHeightLoader />
              </div>
            ) : (
              <Lasso
                itemMarginRight={LassoItemMargin[Layout.Gallery]}
                selectedItems={selectedItems}
                onSelectItems={selectItems}
              >
                {({ saveScrollingComponentWrapperRef, selectedAreaStyle }) => (
                  <div className={filesViewerStyles.scrollArea}>
                    <InfiniteScroll
                      ref={saveScrollingComponentWrapperRef}
                      scrollThreshold="400px"
                      height="100%"
                      dataLength={items.length}
                      hasMore={hasMoreItems}
                      next={loadMoreItems}
                      style={scrollStyle}
                      loader={null}
                    >
                      <FilesGallery
                        items={items}
                        isItemSelected={isItemSelected}
                        isItemDisabled={isItemDisabled}
                        onFolderDoubleClick={openFolder}
                        onFileDoubleClick={submitItemOnDoubleClick}
                        clearItemsSelection={clearItemsSelection}
                        onItemClick={handleItemClick}
                      />

                      {areNextFilesLoading && (
                        <div className={filesViewerStyles.loaderMore}>
                          <Loader size="small" />
                        </div>
                      )}

                      {selectedAreaStyle && (
                        <div
                          className={lassoSelectedAreaStyles.selectedArea}
                          style={selectedAreaStyle}
                          data-element-type="lasso"
                        />
                      )}
                    </InfiniteScroll>
                  </div>
                )}
              </Lasso>
            )}
          </div>

          <div className={styles.addButtonWrapper}>
            <Button
              size="small"
              disabled={!selectedItemsCount}
              onClick={submitSelectedItems}
              dataHook="add-files-to-board-add-button"
            >
              <FormattedMessage
                id="boards.modal.add-files.confirm"
                values={{
                  itemsCount: selectedItemsCount,
                }}
              />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
